import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import ClueGlyph from './ClueGlyph';
import { forNotoEmoji, isIconClue, toDisplayClue, toEditorClue } from './clueUtils';
import { fetchPreview, fetchRatkojatClues, fetchIconUrl } from '../../services/wordInfoService';
import { getIconsForWord, getIconsFromClues } from '../../services/iconCluesService';
import { searchIcons } from '../../services/iconSearchService';

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// One icon button: Material Symbol, emoji or custom SVG. The name is in the tooltip.
const IconTile = ({ type, name, value, label, onPick }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let live = true;
    if (type === 'custom') fetchIconUrl(name).then(u => live && setUrl(u));
    return () => { live = false; };
  }, [type, name]);

  return (
    <button type="button" className="wd-icon" onClick={() => onPick(value)} title={label || value} aria-label={label || value}>
      {type === 'symbol' && <span className="material-symbols-rounded">{name}</span>}
      {type === 'emoji' && <span className="glyph-emoji">{forNotoEmoji(value)}</span>}
      {type === 'custom' && (url ? <img src={url} alt="" /> : <span className="glyph-placeholder" />)}
    </button>
  );
};

const SkeletonChips = ({ n = 6 }) => (
  <div className="wd-chips" aria-hidden="true">
    {Array.from({ length: n }, (_, i) => <span key={i} className="wd-chip wd-chip-skeleton" style={{ width: 48 + ((i * 37) % 70) }} />)}
  </div>
);

const MIN_WIDTH = 440;
const MIN_BELOW = 140;
const GAP = 4;

// Fixed position under the anchor (flipped above when there's more room there, unless
// placement is "below"), at least MIN_WIDTH wide and kept inside the visible viewport
// (which shrinks when a phone keyboard is up).
const useAnchoredPosition = (anchor, placement, deps) => {
  const [style, setStyle] = useState(null);
  useLayoutEffect(() => {
    if (!anchor) return;
    const vv = window.visualViewport;
    const update = () => {
      const r = anchor.getBoundingClientRect();
      const vw = window.innerWidth, vh = vv ? vv.height + vv.offsetTop : window.innerHeight;
      const width = Math.min(Math.max(r.width, MIN_WIDTH), vw - 16);
      const left = Math.max(8, Math.min(r.left, vw - width - 8));
      const below = vh - r.bottom - GAP - 8;
      const above = r.top - GAP - 8;
      setStyle(placement === 'below' || below >= 280 || below >= above
        ? { left, width, top: r.bottom + GAP, maxHeight: Math.max(below, MIN_BELOW) }
        : { left, width, bottom: window.innerHeight - r.top + GAP, maxHeight: above });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    vv?.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      vv?.removeEventListener('resize', update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor, placement, ...deps]);
  return style;
};

// Suggestion dropdown for the focused clue: meaning, clues used in earlier Norppa
// puzzles, icon ideas and Ratkojat clues as compact pills. `query` is what the user
// has typed since focusing the clue; it searches icons and narrows the clue lists.
// `anchor` is the clue's card; the dropdown opens under it.
const WordDesk = ({ word, query, pastClues, onApply, anchor, placement = 'auto', onClose }) => {
  const ref = useRef(null);
  const answer = word?.answer || '';
  const [preview, setPreview] = useState(undefined);
  const [ratkojat, setRatkojat] = useState(undefined);
  const [iconIdeas, setIconIdeas] = useState([]);
  const [iconHits, setIconHits] = useState([]);

  useEffect(() => {
    if (!answer) return;
    let live = true;
    setPreview(undefined);
    setRatkojat(undefined);
    setIconIdeas([]);

    fetchPreview(answer).then(p => live && setPreview(p));

    getIconsForWord(answer)
      .catch(() => [])
      .then(direct => {
        if (live) setIconIdeas(direct);
        return fetchRatkojatClues(answer).then(clues => {
          if (!live) return;
          setRatkojat(clues);
          if (Array.isArray(clues) && clues.length) {
            getIconsFromClues(clues, direct)
              .then(indirect => live && indirect.length && setIconIdeas([...direct, ...indirect]))
              .catch(() => {});
          }
        });
      });

    return () => { live = false; };
  }, [answer]);

  const search = query.trim();
  const searchable = search.length >= 2 && !search.startsWith('{') && !isIconClue(search);

  useEffect(() => {
    if (!searchable) { setIconHits([]); return; }
    let live = true;
    const t = setTimeout(() => {
      searchIcons(search).then(r => live && setIconHits(r)).catch(() => live && setIconHits([]));
    }, 180);
    return () => { live = false; clearTimeout(t); };
  }, [search, searchable]);

  const position = useAnchoredPosition(anchor, placement, [answer, query]);

  // Closes on Escape and on clicks outside both the dropdown and its clue card.
  useEffect(() => {
    if (!anchor) return;
    const onDown = e => {
      if (ref.current?.contains(e.target) || anchor.contains(e.target)) return;
      onClose();
    };
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [anchor, onClose]);

  if (!word || !anchor || !position) return null;

  const needle = searchable ? search.toLowerCase() : '';
  const matches = (text) => !needle || toDisplayClue(text).toLowerCase().includes(needle);

  const icons = searchable ? iconHits : iconIdeas;
  const past = [...new Set(pastClues.map(p => p.clue))].filter(matches);
  const ratkojatList = Array.isArray(ratkojat) ? ratkojat.filter(matches) : [];
  const gloss = preview && !preview.error ? (preview.glossesFi || preview.glossesEn)?.[0] : null;

  if (!gloss && !icons.length && !past.length && ratkojat !== undefined && !ratkojatList.length) return null;

  return ReactDOM.createPortal(
    // mousedown is prevented so the clue field keeps focus while picking.
    <div className="word-desk" ref={ref} style={position} onMouseDown={e => e.preventDefault()} role="dialog" aria-label={`Suggestions for ${answer}`}>
      <button type="button" className="wd-close" onClick={onClose} aria-label="Close suggestions">
        <span className="material-symbols-outlined" aria-hidden="true">close</span>
      </button>
      {gloss && <div className="wd-def">{gloss}</div>}
      <div className="wd-scroll">
        {icons.length > 0 && (
          <div className="wd-group wd-icons">{icons.map(ic => (
            <IconTile key={ic.id || ic.value} type={ic.type} name={ic.icon || ic.name} value={ic.value}
              label={ic.display || (ic.type === 'emoji' ? '' : ic.name)} onPick={onApply} />
          ))}</div>
        )}
        {past.length > 0 && (
          <div className="wd-group wd-chips">{past.map(clue => (
            <button type="button" key={clue} className="wd-chip wd-chip-past" onClick={() => onApply(toEditorClue(clue))}>
              <ClueGlyph clue={clue} />
            </button>
          ))}</div>
        )}
        {ratkojat === undefined ? <div className="wd-group"><SkeletonChips /></div>
          : ratkojatList.length > 0 && (
            <div className="wd-group wd-chips">{ratkojatList.map((c, i) => (
              <button type="button" key={i} className="wd-chip" onClick={() => onApply(capitalize(c))}>{c}</button>
            ))}</div>
          )}
      </div>
    </div>,
    document.body
  );
};

export default WordDesk;
